import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Alert,
} from '@mui/material'
import { t } from '@lingui/macro'
import type { GeneralAttendanceRow } from '@/core/api/statistics'
import {
  GENERAL_AGE_BUCKETS, GENDERS,
  getGenderLabel, pivotGeneralAttendance, computeAttendanceTotals,
} from '../../lib/monthlyReportHelpers'

type Props = {
  data?: GeneralAttendanceRow[]
  isLoading: boolean
  error?: unknown
  multiEstablishment: boolean
}

export function GeneralAttendanceTable({ data, isLoading, error, multiEstablishment }: Props) {
  if (isLoading) return <CircularProgress size={24} />
  if (error) return <Alert severity="error">{t`Error loading attendance data`}</Alert>
  if (!data || data.length === 0) return <Typography color="text.secondary">{t`No data available`}</Typography>

  const rows = pivotGeneralAttendance(data)
  const totalsRow = computeAttendanceTotals(rows)
  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0)

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'primary.main' }}>
            {multiEstablishment && (
              <TableCell rowSpan={2} sx={{ color: 'white', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.3)' }}>
                {t`Location`}
              </TableCell>
            )}
            {GENDERS.map(g => (
              <TableCell
                key={g}
                align="center"
                colSpan={GENERAL_AGE_BUCKETS.length}
                sx={{ color: 'white', fontWeight: 'bold', borderRight: '1px solid rgba(255,255,255,0.3)' }}
              >
                {getGenderLabel(g)}
              </TableCell>
            ))}
            <TableCell rowSpan={2} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t`Total`}
            </TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: 'primary.dark' }}>
            {GENDERS.map(g =>
              GENERAL_AGE_BUCKETS.map(bucket => (
                <TableCell key={`${g}-${bucket}`} align="center" sx={{ color: 'white', fontSize: '0.75rem', py: 0.5 }}>
                  {bucket}
                </TableCell>
              ))
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.establishmentName} hover>
              {multiEstablishment && (
                <TableCell sx={{ fontWeight: 500, borderRight: '1px solid rgba(0,0,0,0.12)' }}>
                  {row.establishmentName}
                </TableCell>
              )}
              {GENDERS.map(g =>
                GENERAL_AGE_BUCKETS.map(bucket => (
                  <TableCell key={`${g}-${bucket}`} align="center">
                    {row.cells[`${g}|${bucket}`] || 0}
                  </TableCell>
                ))
              )}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>{row.total}</TableCell>
            </TableRow>
          ))}
          {multiEstablishment && (
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid rgba(0,0,0,0.12)' }}>{t`Total`}</TableCell>
              {GENDERS.map(g =>
                GENERAL_AGE_BUCKETS.map(bucket => (
                  <TableCell key={`total-${g}-${bucket}`} align="center" sx={{ fontWeight: 'bold' }}>
                    {totalsRow[`${g}|${bucket}`] || 0}
                  </TableCell>
                ))
              )}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>{grandTotal}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
